<template>
  <div class="flex flex-col overflow-hidden bg-gray-900">
    <div class="header">
      <div class="pt-2 pr-2 ml-auto text-xs cursor-pointer" @click="goToSite">
        <ArrowTopRightOnSquareIcon class="external-icon" />Go to Project
      </div>
      <div class="flex justify-center">Hotspots</div>
    </div>
    <SearchInput
      v-if="showSearch"
      v-model="search"
      :disabled="disabled"
      name="model-layer"
      data-testid="layer-list-search"
      class="text-white bg-gray-700"
    />
    <ul v-if="filteredHotspots && filteredHotspots.length" class="m-0 overflow-y-auto bg-gray-900 border-b border-gray-700">
      <li v-for="(hotspot, hotspotIndex) in filteredHotspots" :key="hotspotIndex" class="hotspot">
        <EditorDeckSlidesThreeDLayerListItem
          class="group h-9"
          :selected="selectedHotspots.some(h => h.id === hotspot.id)"
          :visible="!hiddenHotspotIds.includes(hotspot.id)"
          @set-visible="!disabled && $emit('set-hotspot-visibility', { hotspotId: hotspot.id, visible: $event })"
          @click="$emit('click', $event, hotspot)"
          @click.right="clickIfNotSelected($event, hotspot)"
          @contextmenu.stop.prevent="!disabled && $emit('contextmenu', $event, getSelectionFor(hotspot))"
        >
          <div class="flex items-center flex-1 w-full hotspot-title" :class="{ 'pointer-events-none': disabled }">
            <div class="px-2 truncate cursor-pointer" style="max-width: calc(100% - 2rem)">
              <ThreeDIcon v-if="hotspot.media[0].type === '3'" />
              <ImageIcon v-if="hotspot.media[0].type === 'I'" />
              <PdfIcon v-if="hotspot.media[0].type === 'P'" />
              <SiteIcon v-if="hotspot.media[0].type === 'L'" />
              <FilmIcon v-if="hotspot.media[0].type === 'V'" />
              <InfoIcon v-if="hotspot.media[0].type === 'N'" />
              <LocationIcon v-if="hotspot.media[0].type === 'M'" />
              {{ hotspot.title }}
            </div>
          </div>
        </EditorDeckSlidesThreeDLayerListItem>
      </li>
    </ul>
    <div v-else class="flex-grow pt-4 text-sm font-light text-center text-gray-500 bg-gray-900">
      <XCircleIcon class="w-10 h-10 inline-block" />
      <h4 class="mt-2 mb-0 text-lg font-light" data-testid="no-results-message">Sorry!</h4>
      <p class="m-0">No hotspots were found.</p>
      <p class="m-0">Please try some other keywords.</p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { computed, defineComponent, ref, watch } from 'vue'
import { XCircleIcon } from '@heroicons/vue/24/solid'
import { FilmIcon } from '@heroicons/vue/24/outline'
import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/24/outline'

import type { Hotspot } from '@/entities/site'
import ThreeDIcon from '@/components/icons/ThreeDSlideIcon.svg'
import LocationIcon from '@/components/icons/site/hotspot-location.svg'
import ImageIcon from '@/components/icons/site/hotspot-image.svg'
import InfoIcon from '@/components/icons/site/hotspot-info.svg'
import PdfIcon from '@/components/icons/site/hotspot-pdf.svg'
import SiteIcon from '@/components/icons/site/hotspot-360.svg'
import { makeEscapedRegExp } from '@/lib/utils'

const props = defineProps({
  hotspots: {
    type: Array as PropType<Hotspot[]>,
    required: true,
  },
  selectedHotspots: {
    type: Array as PropType<DeepReadonly<Hotspot[]>>,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showSearch: {
    type: Boolean,
    default: true,
  },
  hiddenHotspotIds: {
    type: Array as PropType<(number | undefined)[]>,
    default: () => [],
  },
  goToSite: {
    type: Function as PropType<(payload: MouseEvent) => void>,
    required: true,
  },
})

const emit = defineEmits([
  'set-group-visible',
  'set-hotspot-visibility',
  'layer-hover',
  'contextmenu',
  'layer-search',
  'click',
  'manage-visibility',
  'hotspot-search',
])

const search = ref('')

const filteredHotspots = computed(() => {
  if (!props.hotspots) return []
  const searchFilters = search.value.split(' ').map(makeEscapedRegExp)
  return props.hotspots.filter(h => searchFilters.every(x => x.test(h.title)))
})

watch(search, () => {
  emit('hotspot-search')
})

function getSelectionFor(hotspot: Hotspot): Hotspot | Hotspot[] {
  const prevSelectedHotspots = props.selectedHotspots
  const wasHotspotPreviouslySelected = prevSelectedHotspots.some(h => h.id === hotspot.id)

  if (prevSelectedHotspots.length > 1 && wasHotspotPreviouslySelected) {
    return prevSelectedHotspots as Hotspot[]
  }

  return hotspot
}

function clickIfNotSelected(event: MouseEvent, hotspot: Hotspot): void {
  if (!props.selectedHotspots.some(h => h.id === hotspot.id)) {
    emit('click', event, hotspot)
  }
}
</script>

<style lang="stylus" scoped>
.header
  @apply flex flex-col
  height: 66px
  background-color: rgb(75, 75, 75)

.hotspot-title
  @apply cursor-default select-none m-0
  // These 4 for for no text wrap and ellipsis overflow
  @apply min-w-0 whitespace-nowrap overflow-hidden overflow-ellipsis

  svg
    @apply w-4 h-4 mr-2 text-copper-200 inline-block

.external-icon
  @apply w-4 h-4 inline-block
  margin-right: 4px
  margin-bottom: 3px
</style>
